import { useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { Music2, Play, RotateCcw, SkipForward, Square } from "lucide-react";
import { api } from "@/lib/api";
import { afterIntro } from "@/lib/intro";

const KEY = "wm_music";
const GESTURES = ["pointerdown", "keydown", "touchstart"];
const shuffled = (arr) => [...arr].sort(() => Math.random() - 0.5);
const sig = (t) => (t || []).map((x) => x.id).join(",");

export const MusicPlayer = () => {
  const { pathname } = useLocation();
  const hidden = pathname.startsWith("/admin") || pathname.startsWith("/staff");
  const { data } = useQuery({ queryKey: ["music"], queryFn: async () => (await api.get("/music")).data, refetchInterval: 10 * 60_000, enabled: !hidden });
  const audio = useRef(null);
  const pending = useRef(null);
  const advance = useRef(false);
  const [list, setList] = useState([]);
  const [idx, setIdx] = useState(0);
  const [playing, setPlaying] = useState(false);

  useEffect(() => {
    if (!data) return;
    const next = data.enabled ? (data.shuffle ? shuffled(data.tracks) : data.tracks) : [];
    if (sig(next) === sig(list)) return;
    if (playing && next.length) pending.current = next;
    else { setList(next); setIdx(0); }
  }, [data]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const el = audio.current;
    if (!el || !list.length) return undefined;
    el.volume = data?.volume ?? 0.35;
    if (localStorage.getItem(KEY) === "off") return undefined;
    const start = () => { el.play().catch(() => {}); GESTURES.forEach((g) => document.removeEventListener(g, start)); };
    const off = afterIntro(() => el.play().catch(() => GESTURES.forEach((g) => document.addEventListener(g, start, { once: true }))));
    return () => { off(); GESTURES.forEach((g) => document.removeEventListener(g, start)); };
  }, [list]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (advance.current) { advance.current = false; audio.current?.play().catch(() => {}); }
  }, [idx, list]);

  useEffect(() => { if (hidden && audio.current) audio.current.pause(); }, [hidden]);

  if (hidden || !list.length) return null;
  const track = list[idx % list.length];

  const go = (i) => {
    advance.current = true;
    if (pending.current) { const n = pending.current; pending.current = null; setList(n); setIdx(0); return; }
    if (i === idx % list.length) { audio.current.currentTime = 0; audio.current.play().catch(() => {}); advance.current = false; return; }
    setIdx(i);
  };
  const stop = (e) => {
    e.stopPropagation();
    audio.current.pause();
    localStorage.setItem(KEY, "off");
  };
  const play = (e) => {
    e.stopPropagation();
    localStorage.setItem(KEY, "on");
    audio.current.play().catch(() => {});
  };
  const restart = (e) => {
    e.stopPropagation();
    localStorage.setItem(KEY, "on");
    go(0);
  };
  const next = (e) => { e.stopPropagation(); go((idx + 1) % list.length); };

  return (
    <div className={`music ${playing ? "is-on" : ""}`} data-testid="music-player">
      <audio ref={audio} src={`${process.env.REACT_APP_BACKEND_URL}${track.url}`} preload="auto"
        onEnded={() => go((idx + 1) % list.length)} onPause={() => setPlaying(false)} onPlay={() => setPlaying(true)} data-testid="music-audio" />
      <div className="music-btn" data-testid="music-status">
        <Music2 size={16} />
        <span className="music-text">
          <small>{playing ? "Now playing" : data.label}</small>
          <strong data-testid="music-track-title">{playing ? track.title : "Music paused"}</strong>
        </span>
        {playing && <span className="music-bars" aria-hidden="true"><i /><i /><i /></span>}
      </div>
      {playing
        ? <button type="button" className="music-ctl" onClick={stop} aria-label="Stop music" data-testid="music-stop-button"><Square size={13} fill="currentColor" /></button>
        : <button type="button" className="music-ctl" onClick={play} aria-label="Play music" data-testid="music-play-button"><Play size={15} fill="currentColor" /></button>}
      <button type="button" className="music-ctl" onClick={restart} aria-label="Restart music" data-testid="music-restart-button"><RotateCcw size={15} /></button>
      {playing && list.length > 1 && (
        <button type="button" className="music-ctl" onClick={next} aria-label="Next track" data-testid="music-next-button"><SkipForward size={15} /></button>
      )}
    </div>
  );
};
